// ══════════════════════════════════════════════════════════════
//  ViewToggle — Segmented Control: Tabelle / Split / Scan
//  + Export-Dropdown (CSV / DXF)
// ══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useCallback } from 'react';
import { Table2, Columns2, Box, Download } from 'lucide-react';

export type ViewMode = 'table' | 'split' | 'scan';
export type ExportFormat = 'csv' | 'dxf';

interface ViewToggleProps {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
  rowCount?: number;
  onExport?: (format: ExportFormat) => void;
}

const MODES: { id: ViewMode; label: string; icon: React.ReactNode }[] = [
  { id: 'split', label: 'Split',   icon: <Columns2 size={14} strokeWidth={1.5} /> },
  { id: 'table', label: 'Tabelle', icon: <Table2 size={14} strokeWidth={1.5} /> },
  { id: 'scan',  label: 'Scan',    icon: <Box size={14} strokeWidth={1.5} /> },
];

export function ViewToggle({ mode, onChange, rowCount, onExport }: ViewToggleProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const handleExport = useCallback((format: ExportFormat) => {
    setDropdownOpen(false);
    onExport?.(format);
  }, [onExport]);

  return (
    <div className="rb-toolbar">
      <div className="rb-view-toggle">
        {MODES.map(m => (
          <button
            key={m.id}
            className={`rb-view-toggle__btn${mode === m.id ? ' rb-view-toggle__btn--active' : ''}`}
            onClick={() => onChange(m.id)}
            title={m.label}
          >
            {m.icon}
            <span>{m.label}</span>
          </button>
        ))}
      </div>

      {/* Export Dropdown */}
      {onExport && (
        <div className="rb-export" ref={dropdownRef}>
          <button
            className="rb-export__btn"
            onClick={() => setDropdownOpen(p => !p)}
            title="Exportieren"
          >
            <Download size={14} strokeWidth={1.5} />
            <span>Export</span>
          </button>
          {dropdownOpen && (
            <div className="rb-export__dropdown">
              <button className="rb-export__option" onClick={() => handleExport('csv')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
                Als CSV exportieren
              </button>
              <button className="rb-export__option" onClick={() => handleExport('dxf')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                </svg>
                Als DXF exportieren
              </button>
            </div>
          )}
        </div>
      )}

      <div className="rb-toolbar__spacer" />
      {rowCount != null && (
        <span className="rb-toolbar__info">
          {rowCount} {rowCount === 1 ? 'Raum' : 'Räume'}
        </span>
      )}
    </div>
  );
}
